import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getErrorMessage } from '../lib/errors';
import { generateInviteQR } from '../lib/qr-code';
import CardNB from './ui-nb/CardNB';
import ButtonNB from './ui-nb/ButtonNB';
import SelectNB from './ui-nb/SelectNB';
import { error as logError } from '../lib/logger';

interface EmployeeInviteModalProps {
  branchId?: string | null;
  branchName?: string | null;
  organizationId: string;
  locale: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface InvitationData {
  code: string;
  expires_at: string;
}

interface BranchOption {
  id: string;
  name: string;
}

export default function EmployeeInviteModal({
  branchId,
  branchName,
  organizationId,
  locale,
  onClose,
  onSuccess,
}: EmployeeInviteModalProps) {
  const isBranchInvite = !!branchId;
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'technician' | 'cashier' | 'admin'>('technician');
  const [selectedBranchId, setSelectedBranchId] = useState<string>(branchId || '');
  const [branches, setBranches] = useState<BranchOption[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  // Fetch branches when the modal opens in org-level mode (no branchId prop)
  useEffect(() => {
    if (branchId || !organizationId) return;
    let cancelled = false;
    async function loadBranches() {
      setLoadingBranches(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('branches')
          .select('id, name')
          .eq('organization_id', organizationId)
          .order('name');
        if (!cancelled) {
          if (fetchError) {
            logError('Failed to load branches for invite modal', { error: getErrorMessage(fetchError) });
            setBranches([]);
          } else {
            setBranches(data ?? []);
          }
        }
      } finally {
        if (!cancelled) setLoadingBranches(false);
      }
    }
    loadBranches();
    return () => { cancelled = true; };
  }, [branchId, organizationId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Generate code client-side for reliable QR display
      const code = Array.from({ length: 8 }, () =>
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[Math.floor(Math.random() * 36)]
      ).join('');

      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const insertPayload: Record<string, string> = {
        email: email.trim().toLowerCase(),
        role,
        organization_id: organizationId,
        code,
        expires_at: expiresAt.toISOString(),
      };

      // Resolve the effective branch_id:
      //  - branchInvite: prop wins (locked to a branch)
      //  - org-level: use the selected branch from the dropdown, or omit (no branch)
      const effectiveBranchId = isBranchInvite ? branchId : (selectedBranchId || null);
      if (effectiveBranchId) {
        insertPayload.branch_id = effectiveBranchId;
      }

      // Use .select().single() so the displayed code is the one actually
      // stored in the DB, not the one we generated client-side. The
      // generate_invite_code trigger (migration 055) now respects the
      // caller-supplied code, but we still want defense in depth in case
      // the trigger ever changes again.
      const { data: inserted, error: insertError } = await supabase
        .from('invitation')
        .insert(insertPayload)
        .select('code, expires_at')
        .single();

      if (insertError) throw insertError;

      const storedCode = inserted.code;
      const storedExpiresAt = inserted.expires_at;

      setInvitationData({ code: storedCode, expires_at: storedExpiresAt });

      // Generate QR code
      const qrUrl = await generateInviteQR(storedCode);
      setQrDataUrl(qrUrl);
    } catch (err) {
      logError('Error creating invitation', { error: getErrorMessage(err) });
      setError(getErrorMessage(err) || 'Failed to create invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!invitationData) return;
    const lang = locale || 'en';
    const url = `${window.location.origin}/${lang}/join/${invitationData.code}`;
    navigator.clipboard.writeText(url);
  };

  // Success state — show QR code
  if (invitationData && qrDataUrl) {
    const lang = locale || 'en';
    const inviteUrl = `${window.location.origin}/${lang}/join/${invitationData.code}`;

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
        }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <CardNB style={{ maxWidth: '24rem', width: '100%', padding: '1.5rem', textAlign: 'center' }}>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ width: '3rem', height: '3rem', borderRadius: '9999px', background: 'rgba(0, 0, 0, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
              <svg style={{ width: '1.5rem', height: '1.5rem', color: '#000000' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h3 className="font-nb font-bold text-nb-black mb-1" style={{ fontSize: '1.25rem' }}>
            Invitation created
          </h3>
          <p className="text-nb-black/60 text-sm mb-6">
            Scan the QR code or share the link with {email}
          </p>

          <div style={{ marginBottom: '1.5rem' }}>
            <img
              src={qrDataUrl}
              alt="QR Code"
              className="border-3 border-nb-black"
              style={{ width: '180px', height: '180px', margin: '0 auto', borderRadius: 0 }}
            />
          </div>

          <div className="bg-nb-cream border-2 border-nb-black p-3 mb-4 font-mono text-sm text-nb-black break-all">
            {inviteUrl}
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <ButtonNB variant="secondary" onClick={handleCopyLink} style={{ flex: 1 }}>
              Copy link
            </ButtonNB>
            <ButtonNB variant="primary" onClick={onSuccess} style={{ flex: 1 }}>
              Done
            </ButtonNB>
          </div>
        </CardNB>
      </div>
    );
  }

  // Form state
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <CardNB style={{ maxWidth: '24rem', width: '100%', padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 className="font-nb font-bold text-nb-black m-0" style={{ fontSize: '1.125rem' }}>
            {isBranchInvite
              ? `Add Employee to ${branchName}`
              : 'Invite User to Organization'}
          </h3>
          <button
            onClick={onClose}
            className="bg-transparent border-none cursor-pointer text-nb-black/60 hover:text-nb-black font-nb text-2xl leading-none"
            style={{ borderRadius: 0 }}
          >
            ×
          </button>
        </div>

        {error && (
          <div className="bg-red-100 text-nb-red p-3 rounded-sm mb-4 text-sm font-nb border-2 border-nb-red">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label className="block text-sm font-nb font-medium text-nb-black/70 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="employee@example.com"
              className="font-nb w-full p-2 border-2 border-nb-black bg-nb-white text-nb-black text-sm outline-none"
              style={{ borderRadius: 0 }}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-nb font-medium text-nb-black/70 mb-1">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'technician' | 'cashier' | 'admin')}
              className="font-nb w-full p-2 border-2 border-nb-black bg-nb-white text-nb-black text-sm outline-none"
              style={{ borderRadius: 0 }}
              disabled={loading}
            >
              <option value="technician">Technician</option>
              <option value="cashier">Cashier</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {isBranchInvite && branchName && (
            <p className="text-nb-black/50 text-xs -mt-2">
              This user will be assigned to <strong>{branchName}</strong>
            </p>
          )}

          {!isBranchInvite && (
            <div>
              <label className="block text-sm font-nb font-medium text-nb-black/70 mb-1">
                Branch <span className="text-nb-black/40 font-normal">(optional)</span>
              </label>
              <SelectNB
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(e.target.value)}
                placeholder={loadingBranches ? 'Loading branches...' : 'No branch (org-level)'}
                selectSize="md"
                disabled={loadingBranches}
              >
                <option value="">No branch (org-level)</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </SelectNB>
              <p className="text-nb-black/50 text-xs mt-1">
                Assign to a specific branch, or leave as org-level for cross-branch access.
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.5rem' }}>
            <ButtonNB type="button" variant="secondary" onClick={onClose} style={{ flex: 1 }}>
              Cancel
            </ButtonNB>
            <ButtonNB type="submit" variant="primary" loading={loading} style={{ flex: 1 }}>
              {loading ? 'Creating...' : 'Create invitation'}
            </ButtonNB>
          </div>
        </form>
      </CardNB>
    </div>
  );
}
